import type { Ref } from "react";
import { Loader2Icon } from "lucide-react";
import { Button, ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function LoaderButton({
  children,
  isLoading,
  className,
  type,
  disabled,
  ref,
  ...props
}: ButtonProps & {
  isLoading: boolean;
  type?: "button" | "submit";
  /**
   * `Button` ya es `forwardRef`, pero esto lo dejaba caer en el camino: sin la
   * ref, un `asChild` de Radix (un `DropdownMenuTrigger`, un `TooltipTrigger`)
   * no puede posicionar ni cerrar contra este botón. React 19 pasa `ref` como
   * una prop más, así que alcanza con declararla y reenviarla.
   */
  ref?: Ref<HTMLButtonElement>;
}) {
  // disabled efectivo = OR entre lo pedido por el caller y el loading interno.
  // Antes el `disabled={isLoading}` se ponía ANTES del spread y cualquier
  // `disabled` del caller lo pisaba — eso permitía doble-click durante el
  // request en vuelo (mutation duplicada). Ahora el lock por loading no se
  // puede deshabilitar accidentalmente desde afuera.
  // El spinner está SIEMPRE en el DOM y sólo se muestra u oculta (#279).
  //
  // Montarlo al pasar a cargando era `insertBefore(spinner, hijo)` contra el
  // primer hijo del botón, que casi siempre es un texto suelto ("Guardar"). Con
  // la página traducida por el navegador, ese nodo de texto ya no está —Google
  // Translate lo reemplaza por un `<font>`— y React rompía la pantalla entera
  // ("Failed to execute 'insertBefore' on 'Node'"). Sin inserción no hay nada
  // que pueda fallar. `hidden` es `display: none`: no ocupa lugar ni suma gap.
  return (
    <Button
      ref={ref}
      type={type || "button"}
      {...props}
      disabled={isLoading || disabled}
      className={cn("flex justify-center gap-2 px-3", className)}
    >
      <Loader2Icon
        className={cn("h-4 w-4 animate-spin", !isLoading && "hidden")}
        aria-hidden
      />
      {children}
    </Button>
  );
}
